"use client"

import { axiosInstance } from '@repo/ui/lib/api/axios-instance';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: async (payload: { groupeId: number; planType: 'BASIC' | 'ADVANCED'; tierId: string; promoCode?: string; successUrl?: string; cancelUrl?: string }) => {
      const { data } = await axiosInstance.post('/payments/create-checkout-session', payload);
      return data.data as { url: string; id: string };
    }
  });
}

export function useSwitchPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { groupeId: number; planType: 'BASIC' | 'ADVANCED'; tierId: string; prorationBehavior?: 'now' | 'next_cycle' }) => {
      const { data } = await axiosInstance.post('/payments/switch-plan', payload);
      return data.data as { subscriptionId: string };
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['payments', 'status', vars.groupeId] });
      qc.invalidateQueries({ queryKey: ['payments', 'history', vars.groupeId] });
      qc.invalidateQueries({ queryKey: ['group-subscription', vars.groupeId] });
      qc.invalidateQueries({ queryKey: ['group-usage-stats', vars.groupeId] });
    }
  });
}

export function useSubscriptionStatus(groupeId?: number) {
  return useQuery({
    queryKey: ['payments', 'status', groupeId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/payments/subscription-status/${groupeId}`);
      return data.data as { status: string; currentPeriodEnd?: string; inGracePeriod: boolean; graceEndsAt?: string };
    },
    enabled: !!groupeId,
    staleTime: 60_000,
  });
}

export function usePaymentHistory(groupeId?: number) {
  return useQuery({
    queryKey: ['payments', 'history', groupeId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/payments/history/${groupeId}`);
      return data.data as Array<{
        id: string;
        amount_total: number;
        currency: string;
        status: string;
        created: string;
        invoice_pdf?: string | null;
        hosted_invoice_url?: string | null;
      }>;
    },
    enabled: !!groupeId,
    staleTime: 60_000,
  });
}

export function useCreatePortalSession() {
  return useMutation({
    mutationFn: async (payload: { groupeId: number; returnUrl?: string }) => {
      const { data } = await axiosInstance.post('/payments/create-portal-session', payload);
      return data.data as { url: string };
    }
  });
}


